import {
  between, maxLength, minLength, integer, requiredIf,
} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { StringMap, TypeCampaignEnum } from '@/types';
import {
  checkIfTemplateOrRedirectContainsVoucher,
  getTemplateAndRedirect,
  reusePreviousCouponValues,
  voucherMethodsValues,
  voucherTypeValues,
  migrateOldVoucherConfig,
  buildTemplateVouchersRules,
  TemplateVouchersData,
  getDefaultEnabledOperator,
} from '@/composables/flows';
import { asInt } from '@/helpers';
import { getDefaultUtmFields } from '@/components/flows/operators/shared/utmDefaults';
import type { FlowOperatorMetadata, ValidationResult } from '@/types/flow-types';
import Form from './Form.vue';

export interface SendPushNotificationData {
  template_display_selector_id: number | null;
  reuse_a_previous_coupon: number;
  display_popup_on_site: number;
  template_display_reduction_method: number;
  template_display_reduction_descript: StringMap;
  template_display_reduction_type: number;
  template_display_reduction_identifiant: string;
  template_display_reduction_amount: number;
  template_display_reduction_duplicate: string;
  template_display_reduction_minorderamount: number;
  template_display_reduction_validtime: number;
  template_display_reduction_prefix: string;
  template_vouchers?: TemplateVouchersData;
  template_display_message_langs: string;
  enabled_operator: number;
  [key: string]: any;
}

export function createDefaultData(data: Partial<SendPushNotificationData> = {}, languages: string[] = [], campaignType?: string): SendPushNotificationData {
  return {
    template_display_selector_id: data.template_display_selector_id != null ? asInt(data.template_display_selector_id) : null,
    reuse_a_previous_coupon: asInt(data.reuse_a_previous_coupon ?? 0),
    display_popup_on_site: asInt(data.display_popup_on_site ?? 0),
    template_display_reduction_method: asInt(data.template_display_reduction_method ?? 3),
    template_display_reduction_descript: data.template_display_reduction_descript ?? {},
    template_display_reduction_type: asInt(data.template_display_reduction_type ?? 1),
    template_display_reduction_identifiant: data.template_display_reduction_identifiant ?? '',
    template_display_reduction_amount: asInt(data.template_display_reduction_amount ?? 0),
    template_display_reduction_duplicate: data.template_display_reduction_duplicate ?? '',
    template_display_reduction_minorderamount: asInt(data.template_display_reduction_minorderamount ?? 0),
    template_display_reduction_validtime: asInt(data.template_display_reduction_validtime ?? 7),
    template_display_reduction_prefix: data.template_display_reduction_prefix ?? '',
    template_vouchers: data.template_vouchers && typeof data.template_vouchers === 'object' ? data.template_vouchers : {},
    template_display_message_langs: data.template_display_message_langs ?? JSON.stringify(languages),
    enabled_operator: asInt(data.enabled_operator ?? getDefaultEnabledOperator('boxsendpushnotification', campaignType)),
    ...getDefaultUtmFields(data),
  };
}

interface ValidateContext {
  shopId: number;
}

const SendPushNotification: FlowOperatorMetadata<SendPushNotificationData> = {
  id: 'boxsendpushnotification',
  kind: 'action',
  label: 'automatedScenarios.actions.sendPushNotification.label',
  icon: 'fa-bell',
  hasConfiguration: true,
  availableIn: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
  canToggleEnabled: true,
  requiredServices: ['PushNotificationServiceParameter'],

  menuActions: [
    {
      id: 'send-test-push',
      label: 'campaigns.common.operatorActions.sendTestMessage',
      icon: 'far fa-paper-plane',
      action: 'send-test-push',
    },
  ],

  incompatibilities: {
    [TypeCampaignEnum.AUTOMATION]: {
      nextOperators: [
        'boxabandonpanier', 'boxsitepasstime', 'boxquitsite',
        'boxperiodiclaunching', 'boxscrollpage', 'boxstatuscommand',
        'boxcustomjs', 'registeronsite', 'subscribetonewsletter',
        'boxdisplayfreepopup', 'boxdisplayfreemodel', 'boxsendpushnotification',
        'boxvisitpage',
      ],
    },
    [TypeCampaignEnum.BULK]: {
      nextOperators: [
        'boxabandonpanier', 'boxsitepasstime', 'boxquitsite',
        'boxperiodiclaunching', 'boxscrollpage', 'boxstatuscommand',
        'boxcustomjs', 'registeronsite', 'subscribetonewsletter',
        'boxdisplayfreepopup', 'boxdisplayfreemodel', 'boxsendpushnotification',
        'boxvisitpage', 'boxchoosebulklist', 'boxaudiencesetup',
      ],
    },
  },

  Form,

  defaultData: (data?: Partial<SendPushNotificationData>, campaignType?: string) => createDefaultData(data, [], campaignType),

  outputs: () => 1,

  async validate(data: SendPushNotificationData, context?: ValidateContext): Promise<ValidationResult> {
    let mustConfigVoucher = false;
    let mustConfigMultiVoucher = false;
    const templatesWithVoucher: string[] = [];

    if (data.template_display_selector_id && data.template_display_selector_id > 0 && context?.shopId) {
      const idTemplate = asInt(data.template_display_selector_id);
      let templatesContainsVoucher = false;
      const listTemplate = await getTemplateAndRedirect(context.shopId, idTemplate);
      if (listTemplate !== null) {
        templatesContainsVoucher = checkIfTemplateOrRedirectContainsVoucher(listTemplate);

        // Check if any template has hasVoucher = true in configuration
        listTemplate.forEach((template) => {
          if (typeof template.template_config === 'string') {
            const templateConfig = JSON.parse(template.template_config);
            if (templateConfig.hasVoucher) {
              templatesWithVoucher.push(String(template.id_template));
            }
            // Clean up stale disabled flag if template is no longer conditional
            const tId = String(template.id_template);
            if (data.template_vouchers?.[tId]?.disabled && !templateConfig.hasConditionalVoucher) {
              data.template_vouchers[tId].disabled = false;
            }
          }
        });
      }
      // For multi-voucher system: require config if any template has hasVoucher = true
      if (templatesWithVoucher.length > 0) {
        mustConfigMultiVoucher = true;
      }
      if (templatesContainsVoucher && data.reuse_a_previous_coupon === reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER) {
        mustConfigVoucher = true;
      }
    }

    // Always call this to ensure obsolete configs are removed when templates no longer have vouchers
    migrateOldVoucherConfig(data, templatesWithVoucher);

    const rules: any = {
      template_display_reduction_method: {
        required: mustConfigVoucher,
      },
      template_display_reduction_descript: {
        required: false,
      },
      template_display_reduction_type: {
        required: mustConfigVoucher,
      },
      template_display_reduction_amount: {
        required: requiredIf(() => mustConfigVoucher && [voucherTypeValues.PERCENTAGE, voucherTypeValues.AMOUNT].includes(data.template_display_reduction_type)),
        integer,
      },
      template_display_reduction_minorderamount: {
        required: requiredIf(() => mustConfigVoucher && data.template_display_reduction_method === voucherMethodsValues.AUTOMATIC),
        between: between(0, 10000000),
      },
      template_display_reduction_prefix: {
        minLength: minLength(2),
        maxLength: maxLength(32),
      },
    };

    if (mustConfigVoucher && data.template_display_reduction_method === voucherMethodsValues.MANUAL) {
      rules.template_display_reduction_identifiant = {
        required: true,
        minLength: minLength(2),
        maxLength: maxLength(45),
      };
    }

    if (mustConfigVoucher && data.template_display_reduction_method === voucherMethodsValues.AUTOMATIC && data.template_display_reduction_type === voucherTypeValues.DUPLICATED) {
      rules.template_display_reduction_duplicate = {
        required: true,
        minLength: minLength(2),
        maxLength: maxLength(45),
      };
    }

    if (mustConfigVoucher && data.template_display_reduction_method === voucherMethodsValues.AUTOMATIC) {
      rules.template_display_reduction_validtime = {
        required: true,
        between: between(1, 182),
      };
    }

    // Add dynamic validation rules for template_vouchers (only if multi-voucher system is used)
    if (mustConfigMultiVoucher) {
      Object.assign(rules, buildTemplateVouchersRules(data.template_vouchers, templatesWithVoucher));
    }

    const v$ = useVuelidate(rules, data);
    const success = await v$.value.$validate();

    return {
      success,
      validate: v$,
    };
  },

  canBeDeleted(): boolean {
    return true;
  },
};

export default SendPushNotification;
